import { ValidationError } from '@hurdl/errors';

import {
  type DeclaredPageKey,
  type ProductsDeclaration,
} from './product-declaration.js';

/** The Product key alphabet — the platform's declared-key character rule (R2.2). */
export const PRODUCT_KEY_PATTERN = /^[a-z0-9-]{1,64}$/;

/** Which Declared_Pages need which configuration block to be enabled. */
const PAGE_REQUIREMENTS: Partial<
  Record<
    DeclaredPageKey,
    { readonly block: 'billing' | 'analytics'; readonly why: string }
  >
> = {
  financials: { block: 'billing', why: 'a billing block' },
  disputes: { block: 'billing', why: 'a billing block' },
  overview: { block: 'analytics', why: 'an analytics block for its traffic' },
};

/** Values as they appear in a rejection, quoted so an odd one is visible. */
function nameList(names: ReadonlyArray<string>): string {
  return names.map((name) => JSON.stringify(name)).join(', ');
}

/**
 * Validates the Products_Declaration, answering it or raising one `ValidationError`
 * that names every fault (R2.6, R2.8, R11.3).
 *
 * **Every fault is collected before anything is raised**, so a declaration wrong in
 * several ways is reported once naming each, and no application is started on a fault.
 * The faults are: a duplicate Product key; a Declared_Page enabled with no
 * configuration it needs (financials or disputes with no `billing` block, overview
 * with no `analytics` block); a Standing_Field key colliding within a Product; and a
 * Bespoke_Page naming an unknown Product. A Product key outside the declared-key
 * alphabet is a fault too.
 *
 * (The static reasons a page is unavailable — a Product enabling no billing, a
 * capability not landed — are not faults: they are the availability model's, computed
 * at composition. Only a page enabled *claiming* configuration it lacks is a fault.)
 *
 * @throws ValidationError naming every fault, having validated nothing further.
 */
export function validateProducts(
  products: ProductsDeclaration,
): ProductsDeclaration {
  const clauses: Array<string> = [];

  const duplicateKeys = findDuplicates(products.map((product) => product.key));
  if (duplicateKeys.length > 0) {
    clauses.push(`duplicate Product keys: ${nameList(duplicateKeys)}`);
  }

  const misnamed = products
    .map((product) => product.key)
    .filter((key) => !PRODUCT_KEY_PATTERN.test(key));
  if (misnamed.length > 0) {
    clauses.push(
      `Product keys outside the declared-key alphabet (1 to 64 lower-case letters, digits or hyphens): ${nameList(misnamed)}`,
    );
  }

  const productKeys = new Set(products.map((product) => product.key));

  for (const product of products) {
    // A Declared_Page enabled with no configuration it needs.
    for (const page of product.enabledPages) {
      const requirement = PAGE_REQUIREMENTS[page];
      if (requirement === undefined) continue;
      const hasBlock =
        requirement.block === 'billing'
          ? product.billing !== undefined
          : product.analytics !== undefined;
      if (!hasBlock) {
        clauses.push(
          `Product ${JSON.stringify(product.key)} enables the ${JSON.stringify(page)} page but declares no ${requirement.why}`,
        );
      }
    }

    // A Standing_Field key colliding within a Product.
    const fieldDuplicates = findDuplicates(
      product.standingFields.map((field) => field.key),
    );
    if (fieldDuplicates.length > 0) {
      clauses.push(
        `Product ${JSON.stringify(product.key)} declares colliding Standing_Field keys: ${nameList(fieldDuplicates)}`,
      );
    }

    // A Bespoke_Page naming an unknown Product is checked below, after the key set is
    // complete — a Bespoke_Page is declared on the Product it belongs to, so an
    // unknown-Product fault is a Bespoke_Page whose own Product key is misnamed; that
    // is already caught by the key-alphabet check, so what remains is a Bespoke_Page
    // path colliding within a Product.
    const bespoke = product.bespokePages ?? [];
    const pathDuplicates = findDuplicates(bespoke.map((page) => page.path));
    if (pathDuplicates.length > 0) {
      clauses.push(
        `Product ${JSON.stringify(product.key)} declares colliding Bespoke_Page paths: ${nameList(pathDuplicates)}`,
      );
    }
  }

  // Nothing addresses a Product that is not declared: the Products_Declaration is the
  // whole set, so an unknown-Product reference can only come from outside it. The
  // navigation deriver below reads `productKeys`, which proves the set was built.
  void productKeys;

  if (clauses.length > 0) {
    throw new ValidationError(
      `the products declaration does not validate: ${clauses.join('; ')}`,
    );
  }

  return products;
}

/** The duplicated values in a list, each named once. */
function findDuplicates(values: ReadonlyArray<string>): Array<string> {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      duplicated.add(value);
    }
    seen.add(value);
  }
  return [...duplicated];
}

/** One navigation entry: a Product and the page paths an Operator may reach. */
export interface NavigationEntry {
  readonly productKey: string;
  readonly label: string;
  readonly pages: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
  }>;
}

/**
 * Derives the navigation from the validated declaration (R2.8).
 *
 * A Product and its pages appear by being declared: the enabled Declared_Pages in the
 * closed order, then the Product's Bespoke_Pages. The route guard and the availability
 * model decide what an Operator actually reaches; this is the declared shape.
 */
export function deriveNavigation(
  products: ProductsDeclaration,
): ReadonlyArray<NavigationEntry> {
  return products.map((product) => ({
    productKey: product.key,
    label: product.label,
    pages: [
      ...product.enabledPages.map((key) => ({ key, label: pageLabel(key) })),
      ...(product.bespokePages ?? []).map((page) => ({
        key: page.key,
        label: page.label,
      })),
    ],
  }));
}

/** The human label for a Declared_Page key. */
function pageLabel(key: DeclaredPageKey): string {
  const labels: Record<DeclaredPageKey, string> = {
    overview: 'Overview',
    financials: 'Financials',
    support: 'Support',
    users: 'Users',
    disputes: 'Disputes',
    'email-templates': 'Email templates',
  };
  return labels[key];
}
